import { registerSW } from 'virtual:pwa-register';
import { toast } from 'sonner';

/**
 * Register the service worker, and say something when a new build is waiting.
 *
 * The worker exists to make ShipSwift installable — a real window on the
 * counter PC with the app's own icon, rather than a tab among twenty. The
 * caching it brings is a side effect, and a carefully limited one: the app
 * shell is cached, nothing under /api ever is. See vite.config.js.
 *
 * Updating is a prompt rather than an automatic reload. A reload triggered by a
 * background update is fine on a blog and not fine on a page where somebody is
 * halfway through typing a delivery address. The toast does not auto-dismiss,
 * so the choice waits for them instead of flashing past.
 */

/**
 * How often to ask the browser whether a new worker exists.
 *
 * Without this, nothing asks. A browser checks for an updated service worker on
 * navigation and roughly once a day — and the installed app on a counter PC is
 * opened in the morning and left alone, so it never navigates and never
 * notices. A deploy could sit unseen for a full day while the shop kept using
 * yesterday's bundle, which is exactly what happened.
 *
 * A minute is cheap: one conditional request for a file the size of a
 * stylesheet, and a deploy reaches the counter while whoever asked for it is
 * still watching.
 */
const UPDATE_CHECK_MS = 60_000;

/** Set once the worker is registered, so the update banner can trigger a clean swap. */
let applyUpdate = null;

export function registerServiceWorker() {
  // Only over HTTPS or on localhost — a browser refuses the registration
  // otherwise, and the failure is a console error nobody is looking at.
  if (!('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    onNeedRefresh() {
      toast('A new version of ShipSwift is ready', {
        description: 'Reload when you are between jobs — anything half-typed will be lost.',
        duration: Infinity,
        action: {
          label: 'Reload',
          onClick: () => updateSW(true),
        },
      });
    },

    onRegisteredSW(_url, registration) {
      applyUpdate = () => updateSW(true);

      if (!registration) return;

      const check = () => {
        // Pointless while offline, and it logs a failed fetch every minute.
        if (navigator.onLine === false) return;
        registration.update().catch(() => {
          // Offline, or the server is mid-deploy. The next tick tries again.
        });
      };

      setInterval(check, UPDATE_CHECK_MS);

      // Coming back to the window is the moment someone is most likely to have
      // been told a deploy went out, and the cheapest time to find out.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.addEventListener('online', check);
    },

    onRegisterError(error) {
      // Worth knowing about, not worth interrupting anyone over: the app works
      // perfectly well without the worker, it just is not installable.
      console.error('Service worker registration failed', error);
    },
  });
}

/**
 * Move to the new build now.
 *
 * Prefers handing over to the waiting worker, which swaps the cached shell
 * cleanly. Falls back to a plain reload when there is no worker waiting — the
 * page may be stale for a reason that has nothing to do with the cache, and a
 * reload still fixes it.
 */
export function reloadForUpdate() {
  if (applyUpdate) {
    applyUpdate();
    return;
  }

  window.location.reload();
}
